import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import DateRangeSelector from "../../../Inputs/DateRangeSelector/DateRangeSelector";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { setActionsFrom, setActionsTo } from "../../../../store/actionsDateFiltersSlice";
import { setStoreDateRange } from "../../../../store/analyticsSelections";
import { DateTime } from "luxon";
import { HTTP_ENDPOINTS, WEB_STORAGE_KEYS } from "../../../../clients/clients.constants";
import { WebStorageClient } from "../../../../clients/webStorage.client";
import { useLocation, useNavigate } from "react-router-dom";
import { pathOr } from "ramda";
import { setSiteId } from "../../../../store/siteIdSlice";
import Dropdown from "../../../Inputs/Dropdown/Dropdown";
import Tooltip from "@mui/material/Tooltip";
import { useStyles } from "../../../pages/EquipmentPage/AddModal/AddModalStyles";
import { http } from "../../../../clients/http/http.client";

const TopBar = ({ username, role, image_src }) => {
  const dispatch = useDispatch();
  const [mockedUser, setMockedUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [input, setInput] = useState();
  const location = useLocation();
  const navigate = useNavigate();
  const siteIdStore = useSelector((state) => state.siteId);
  const { instance } = useMsal();
  const [selectedSiteId, setSelectedSiteId] = useState(siteIdStore.siteId);

  const analyticsDateRange = useSelector((state) => state.analyticsSelections.dateRange);
  const actionsDateRange = useSelector((state) => state.actionsDateFilters);

  const queryParams = new URLSearchParams(location.search);
  const view = queryParams.get("view");
  const useAnalyticsSelections =
    location.pathname === "/asset-tracking" &&
    ["trips", "in-transit", "were-in-use", "selected-equipment", "not-used"].includes(view);

  // Convert ISO strings to Date objects for DateRangeSelector, adjusting for UTC display
  const dateRangeValue = useAnalyticsSelections
    ? [
      analyticsDateRange.start
        ? DateTime.fromISO(analyticsDateRange.start).toJSDate()
        : null,
      analyticsDateRange.end
        ? DateTime.fromISO(analyticsDateRange.end).toJSDate()
        : null,
    ]
    : [
      actionsDateRange.from ? DateTime.fromISO(actionsDateRange.from).toJSDate() : null,
      actionsDateRange.to ? DateTime.fromISO(actionsDateRange.to).toJSDate() : null,
    ];

  console.log('dateRangeValue',dateRangeValue);

  // Handle date range changes
  const handleDateRangeChange = (newRange) => {
    if (newRange && newRange.length === 2) {
      const [start, end] = newRange; // newRange is [Date, Date]
      if (useAnalyticsSelections) {
        const from = DateTime.fromJSDate(start).toISO({ includeOffset: false });
        const to = DateTime.fromJSDate(end).toISO({ includeOffset: false });

        dispatch(setStoreDateRange({ start: from, end: to }));
      } else {
        // For actionsDateFiltersSlice, convert to ISO strings
        const from = DateTime.fromJSDate(start).startOf("day").toISO({ includeOffset: false });
        const to = DateTime.fromJSDate(end).endOf("day").toISO({ includeOffset: false });
        localStorage.setItem(WEB_STORAGE_KEYS.ALERTS_DATE_FILTERS, JSON.stringify([from, to]));
        dispatch(setActionsFrom(from));
        dispatch(setActionsTo(to));
      }
    } else {
      if (useAnalyticsSelections) {
        dispatch(setStoreDateRange({ start: null, end: null }));
      } else {
        localStorage.setItem(WEB_STORAGE_KEYS.ALERTS_DATE_FILTERS, JSON.stringify([null, null]));
        dispatch(setActionsFrom(null));
        dispatch(setActionsTo(null));
      }
    }
  };

  const showDateRangePicker = () => {
    const allowedPaths = ["/asset-tracking", "/analytics"];
    const isAllowedPath = allowedPaths.includes(location.pathname);
    const excludedSearchTerms = ["fixtures", "backroom-material", "arrc-inventory", "shopping-carts"];
    const hasExcluded = excludedSearchTerms.some((term) => location.search.includes(term));
    return isAllowedPath && !hasExcluded;
  };

  useEffect(() => {
    setSelectedSiteId(siteIdStore.siteId);
  }, [siteIdStore]);

  const fetchEquipmentAndTagsTypes = async () => {
    try {
      const res = await http.client.get(HTTP_ENDPOINTS.EQUIPMENT_TYPES);
      const siteIds = pathOr([], ["siteIds"], res);
      const mappedSites = siteIds.map((siteId) => ({
        value: siteId.id,
        name: siteId.name,
      }));
      setInput(mappedSites);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchEquipmentAndTagsTypes();
  }, []);

  const userStore = useSelector((state) => state.user);
  useEffect(() => {
    const user = WebStorageClient.getByKey(WEB_STORAGE_KEYS.AUTH_USER);
    if (userStore.user) {
      setMockedUser(userStore.user);
    } else {
      setMockedUser(null);
    }
  }, [userStore.user]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    localStorage.clear();
    navigate("/login");
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/logout`;
    handleMenuClose();
  };

  const handleLogin = () => {
    handleMenuClose();
  };

  const handleRegister = () => {
    handleMenuClose();
  };

  const dialogStyles = useStyles.dialog("#89D8C2");
  const isLoggedIn = Boolean(mockedUser);

  return (
    <AppBar
      position="static"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: "transparent",
        color: "black",
      }}
    >
      <Toolbar sx={{ paddingLeft: "54px!important", paddingRight: "54px!important" }}>
        <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          {showDateRangePicker() && (
            <Box sx={{ marginRight: 2 }}>
              <DateRangeSelector value={dateRangeValue} onChange={handleDateRangeChange} />
            </Box>
          )}
          {!location.search.includes("arrc-inventory") && (
            <Dropdown
              withDebounce={true}
              value={selectedSiteId}
              options={input}
              debounceDelay={300}
              onChangeFn={(value) => {
                dispatch(setSiteId(value));
                localStorage.setItem("site_id", JSON.stringify({ siteId: value }));
              }}
              placeHolder={selectedSiteId ? "Site:" : "Site: All Sites"}
              widthString="300px"
            />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mr: 3 }}>
          <img
            src="https://flagcdn.com/w20/us.png"
            alt="EN"
            style={{ width: "20px", marginRight: "5px" }}
          />
          <Typography variant="body1">EN</Typography>
          <IconButton>
            <ArrowDropDownIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar alt={username} src={image_src || undefined} sx={{ width: 40, height: 40, mr: 2 }}>
            {!image_src && <AccountCircle sx={{ fontSize: 40 }} />}
          </Avatar>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", paddingTop: "5px" }}>
            <Typography variant="body1">{mockedUser?.name || "Guest"}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title={mockedUser?.roles?.join(", ") || ""} arrow>
                <Chip
                  sx={{ height: "27px" }}
                  label={
                    mockedUser?.roles?.length > 0
                      ? `${mockedUser.roles[0]} (+${mockedUser.roles.length - 1})`
                      : "User"
                  }
                  variant="outlined"
                />
              </Tooltip>
            </Box>
          </Box>
          <IconButton onClick={handleMenuClick} sx={{ padding: 0, marginLeft: 1, marginTop: -0.5 }}>
            <ArrowDropDownIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: "visible",
                mt: 1.5,
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            }}
          >
            {isLoggedIn ? (
              <MenuItem onClick={handleLogout}>Log out</MenuItem>
            ) : (
              <>
                <MenuItem onClick={handleLogin}>Log in</MenuItem>
                <MenuItem onClick={handleRegister}>Register</MenuItem>
              </>
            )}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;